import { useEffect, useRef, useState, useCallback } from 'react'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ScannedItem {
  id:         string  // temp local id
  name:       string
  emoji:      string
  category:   string
  location:   string
  expiryDate: string | null
  quantity:   number
  saved:      boolean
  itemId?:    string  // pantry_items.id once saved
  isExisting: boolean // was already in pantry
  needsName?: boolean
}

interface Props {
  householdId: string
  onClose:     () => void
  onDone:      (count: number) => void
}

interface OpenFoodFactsProduct {
  product_name?:    string
  product_name_en?: string
  brands?:          string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function lookupBarcode(barcode: string): Promise<{ rawName: string; brand: string } | null> {
  try {
    const res = await fetch(
      `https://world.openfoodfacts.org/api/v0/product/${barcode}.json`,
      { signal: AbortSignal.timeout(5000) }
    )
    if (!res.ok) return null
    const data = await res.json()
    if (data.status !== 1) return null
    const p: OpenFoodFactsProduct = data.product ?? {}
    const rawName = p.product_name_en || p.product_name || ''
    const brand   = p.brands || ''
    return rawName.trim() ? { rawName: rawName.trim(), brand: brand.trim() } : null
  } catch {
    return null
  }
}

async function cleanWithClaude(barcode: string, rawName: string, brandName: string): Promise<{
  name: string; emoji: string; category: string; location: string; notFound?: boolean
} | null> {
  try {
    const res = await fetch('/api/claude-barcode', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ barcode, rawName, brandName }),
    })
    if (!res.ok) return null
    return await res.json()
  } catch {
    return null
  }
}

// ─── BulkBarcodeScanner ───────────────────────────────────────────────────────

export default function BulkBarcodeScanner({ householdId, onClose, onDone }: Props) {
  const queryClient        = useQueryClient()
  const videoRef           = useRef<HTMLVideoElement>(null)
  const readerRef          = useRef<BrowserMultiFormatReader | null>(null)
  const stopFnRef          = useRef<(() => void) | null>(null)
  const processingRef      = useRef(false)
  const scannedBarcodesRef = useRef<Set<string>>(new Set())

  const [mode,         setMode]         = useState<'barcode' | 'expiry' | 'review'>('barcode')
  const [status,       setStatus]       = useState('Point camera at a barcode')
  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([])
  const [pendingItem,  setPendingItem]  = useState<ScannedItem | null>(null)
  const [processing,   setProcessing]   = useState(false)
  const [savingAll,    setSavingAll]    = useState(false)

  // ── Start barcode scanner ──────────────────────────────────────────────────

  const startBarcodeScanner = useCallback(() => {
    if (!videoRef.current) return
    const reader = new BrowserMultiFormatReader()
    readerRef.current = reader

    reader.decodeFromVideoDevice(undefined, videoRef.current, async (result, _err) => {
      if (!result || processingRef.current) return
      const barcode = result.getText()
      if (scannedBarcodesRef.current.has(barcode)) return

      processingRef.current = true
      scannedBarcodesRef.current.add(barcode)
      setProcessing(true)
      setStatus('Looking up product…')

      try {
        const offResult = await lookupBarcode(barcode)
        const rawName   = offResult?.rawName || ''
        const brand     = offResult?.brand   || ''

        const cleaned = await cleanWithClaude(barcode, rawName, brand)

        if (cleaned?.notFound) {
          setPendingItem({
            id:         crypto.randomUUID(),
            name:       '',
            emoji:      '📦',
            category:   'pantry',
            location:   'pantry',
            expiryDate: null,
            quantity:   1,
            saved:      false,
            isExisting: false,
            needsName:  true,
          })
          setMode('expiry')
          setStatus('Product not found — enter name manually')
          stopFnRef.current?.()
          return
        }

        const newItem: ScannedItem = {
          id:         crypto.randomUUID(),
          name:       cleaned?.name     || rawName || `Item (${barcode.slice(-4)})`,
          emoji:      cleaned?.emoji    || '📦',
          category:   cleaned?.category || 'pantry',
          location:   cleaned?.location || 'pantry',
          expiryDate: null,
          quantity:   1,
          saved:      false,
          isExisting: false,
        }

        const { data: existing } = await supabase
          .from('pantry_items')
          .select('id, name, emoji')
          .eq('household_id', householdId)
          .ilike('name', newItem.name)
          .limit(1)
          .maybeSingle()

        if (existing) {
          newItem.isExisting = true
          newItem.itemId     = existing.id
          newItem.name       = existing.name
          newItem.emoji      = existing.emoji
        }

        setPendingItem(newItem)
        setMode('expiry')
        setStatus(`Found: ${newItem.name}`)
        stopFnRef.current?.()

      } catch (err) {
        console.error('Barcode processing error:', err)
        setStatus('Could not identify product — try again')
        processingRef.current = false
        scannedBarcodesRef.current.delete(barcode)
      } finally {
        setProcessing(false)
      }
    }).then(controls => {
      stopFnRef.current = () => controls.stop()
    }).catch(() => {
      setStatus('Camera not available — check permissions')
    })
  }, [householdId])

  useEffect(() => {
    startBarcodeScanner()
    return () => { stopFnRef.current?.() }
  }, [startBarcodeScanner])

  // ── Confirm item and return to barcode scanner ─────────────────────────────

  function confirmAndContinue(expiryDate: string | null = null) {
    if (!pendingItem) return
    const finalItem = { ...pendingItem, expiryDate: expiryDate ?? pendingItem.expiryDate }
    setScannedItems(prev => [...prev, finalItem])
    setPendingItem(null)
    processingRef.current = false
    setMode('barcode')
    setStatus('Point camera at a barcode')
    startBarcodeScanner()
  }

  // ── Adjust quantity ────────────────────────────────────────────────────────

  function adjustQty(id: string, delta: number) {
    setScannedItems(prev =>
      prev.map(i => i.id === id ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i)
    )
  }

  function removeItem(id: string) {
    setScannedItems(prev => prev.filter(i => i.id !== id))
  }

  // ── Save all to pantry ─────────────────────────────────────────────────────

  async function handleSaveAll() {
    if (scannedItems.length === 0) return
    setSavingAll(true)

    try {
      const now = new Date().toISOString()

      for (const item of scannedItems) {
        if (item.isExisting && item.itemId) {
          await supabase.from('item_batches').insert({
            item_id:      item.itemId,
            household_id: householdId,
            quantity:     item.quantity,
            expiry_date:  item.expiryDate || null,
            status:       'active',
          })
          await supabase.from('pantry_items').update({
            last_confirmed_at: now,
            status_override:   null,
          }).eq('id', item.itemId)
        } else {
          const { data: newItem } = await supabase
            .from('pantry_items')
            .insert({
              household_id:            householdId,
              name:                    item.name,
              emoji:                   item.emoji,
              display_category:        item.category,
              freshness_category:      getCategoryFreshness(item.category),
              is_active:               true,
              confirmation_count:      3,
              last_confirmed_at:       now,
              location:                item.location,
              track_expiration:        !!item.expiryDate,
              current_expiration_date: item.expiryDate || null,
              status_override:         null,
            })
            .select('id')
            .single()

          if (newItem && item.quantity > 0) {
            await supabase.from('item_batches').insert({
              item_id:      newItem.id,
              household_id: householdId,
              quantity:     item.quantity,
              expiry_date:  item.expiryDate || null,
              status:       'active',
            })
          }
        }
      }

      queryClient.invalidateQueries({ queryKey: ['pantry-items', householdId] })
      onDone(scannedItems.length)

    } catch (err) {
      console.error('Save error:', err)
    } finally {
      setSavingAll(false)
    }
  }

  function getCategoryFreshness(cat: string): string {
    switch (cat) {
      case 'produce':    return 'fresh_produce'
      case 'dairy':      return 'dairy'
      case 'meat':       return 'meat_fish'
      case 'bakery':     return 'bread_bakery'
      case 'frozen':     return 'frozen'
      case 'condiments': return 'pantry_staple'
      case 'cleaning':   return 'pantry_staple'
      default:           return 'pantry_staple'
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col max-w-[480px] mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/80 shrink-0">
        <div>
          <p className="text-white text-sm font-semibold">
            {mode === 'barcode' ? 'Bulk Scanner' :
             mode === 'expiry'  ? 'Expiry Date' : 'Review Items'}
          </p>
          {scannedItems.length > 0 && (
            <p className="text-[#6B9E78] text-xs">{scannedItems.length} scanned</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {scannedItems.length > 0 && mode === 'barcode' && (
            <button
              onClick={() => setMode('review')}
              className="text-xs font-semibold text-white bg-[#6B9E78] px-3 py-1.5 rounded-lg"
            >
              Review ({scannedItems.length})
            </button>
          )}
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white text-sm"
          >
            ✕
          </button>
        </div>
      </div>

      {/* ── Barcode mode ── */}
      {mode === 'barcode' && (
        <>
          <div className="flex-1 relative overflow-hidden">
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              muted
              playsInline
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/40" />
              <div
                className="relative z-10 w-72 h-36 rounded-2xl"
                style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)' }}
              >
                <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#6B9E78] rounded-tl-lg" />
                <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[#6B9E78] rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#6B9E78] rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#6B9E78] rounded-br-lg" />
              </div>
            </div>
          </div>

          {/* Recently scanned items */}
          {scannedItems.length > 0 && (
            <div className="bg-black/80 px-4 py-2 max-h-32 overflow-y-auto">
              {[...scannedItems].reverse().slice(0, 3).map(item => (
                <div key={item.id} className="flex items-center gap-2 py-1.5">
                  <span className="text-base">{item.emoji}</span>
                  <span className="text-white text-xs flex-1 truncate">{item.name}</span>
                  {item.expiryDate && (
                    <span className="text-[#6B9E78] text-[10px]">exp {item.expiryDate}</span>
                  )}
                  <span className="text-[#6B9E78] text-xs">✓</span>
                </div>
              ))}
            </div>
          )}

          <div className="px-6 py-4 text-center bg-[#111]">
            {processing ? (
              <div className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 rounded-full border-2 border-[#6B9E78] border-t-transparent animate-spin" />
                <p className="text-[#6B9E78] text-sm font-medium">{status}</p>
              </div>
            ) : (
              <p className="text-[#B0B0B0] text-sm">{status}</p>
            )}
          </div>
        </>
      )}

      {/* ── Expiry entry mode ── */}
      {mode === 'expiry' && pendingItem && (
        <>
          {/* Found item banner */}
          <div className="bg-[#1a2e1d] px-4 py-3 flex items-center gap-3 shrink-0">
            <span className="text-2xl">{pendingItem.emoji}</span>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold truncate">{pendingItem.name}</p>
              <p className="text-[#6B9E78] text-xs">
                {pendingItem.isExisting ? 'Already in pantry — adding batch' : 'New item'}
              </p>
            </div>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6 bg-[#FAF8F4]">

            {/* Name input — shown when product not found in database */}
            {pendingItem.needsName && (
              <div className="w-full">
                <p className="text-sm font-semibold text-[#2C2A26] mb-2">What is this product?</p>
                <input
                  type="text"
                  placeholder="e.g. Cheez-It Crackers"
                  autoFocus
                  className="w-full px-4 py-3 rounded-2xl border-2 border-[#6B9E78] bg-white
                             text-[#2C2A26] text-sm outline-none font-medium"
                  onChange={e => setPendingItem(prev => prev ? { ...prev, name: e.target.value } : prev)}
                />
              </div>
            )}

            <div className="text-center">
              <span className="text-5xl">📅</span>
              <p className="text-lg font-semibold text-[#2C2A26] mt-3">Expiry date?</p>
              <p className="text-sm text-[#9A9491] mt-1">Check the packaging and enter it below</p>
            </div>

            <input
              type="date"
              min={new Date().toISOString().split('T')[0]}
              max="2099-12-31"
              autoFocus
              onChange={e => {
                if (e.target.value) {
                  setPendingItem(prev => prev ? { ...prev, expiryDate: e.target.value } : prev)
                }
              }}
              defaultValue={pendingItem.expiryDate ?? ''}
              className="w-full px-4 py-3.5 rounded-2xl border-2 border-[#6B9E78] bg-white
                         text-[#2C2A26] text-sm outline-none text-center font-medium"
            />

            {/* Location picker */}
            <div className="w-full">
              <p className="text-xs font-semibold text-[#9A9491] uppercase tracking-wide mb-2 text-center">
                Where does it go?
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'fridge',     emoji: '🧊', label: 'Fridge' },
                  { id: 'freezer',    emoji: '❄️', label: 'Freezer' },
                  { id: 'pantry',     emoji: '🏠', label: 'Pantry' },
                  { id: 'cabinets',   emoji: '🪟', label: 'Cabinets' },
                  { id: 'countertop', emoji: '🍳', label: 'Countertop' },
                  { id: 'other',      emoji: '📦', label: 'Other' },
                ].map(loc => (
                  <button
                    key={loc.id}
                    onClick={() => setPendingItem(prev => prev ? { ...prev, location: loc.id } : prev)}
                    className="flex flex-col items-center gap-1 py-2.5 rounded-xl text-xs font-medium
                               transition-all"
                    style={{
                      background: pendingItem.location === loc.id ? '#E8F2EA' : '#F2EDE6',
                      color:      pendingItem.location === loc.id ? '#6B9E78' : '#7A7468',
                      border:     pendingItem.location === loc.id ? '1.5px solid #6B9E78' : '1.5px solid transparent',
                    }}
                  >
                    <span className="text-lg">{loc.emoji}</span>
                    <span>{loc.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="w-full flex flex-col gap-2">
              <button
                onClick={() => confirmAndContinue(pendingItem.expiryDate)}
                disabled={pendingItem.needsName && !pendingItem.name.trim()}
                className="w-full py-4 rounded-2xl text-white font-semibold text-base disabled:opacity-40"
                style={{ background: '#6B9E78' }}
              >
                {pendingItem.needsName && !pendingItem.name.trim()
                  ? 'Enter a name first'
                  : pendingItem.expiryDate ? 'Confirm →' : 'Skip — no expiry date'}
              </button>
              {pendingItem.expiryDate && (
                <button
                  onClick={() => {
                    setPendingItem(prev => prev ? { ...prev, expiryDate: null } : prev)
                    confirmAndContinue(null)
                  }}
                  className="w-full py-3 rounded-2xl text-[#9A9491] text-sm border border-[#E8E2DC]"
                >
                  Skip expiry date
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Review mode ── */}
      {mode === 'review' && (
        <div className="flex-1 overflow-y-auto bg-[#FAF8F4]">
          <div className="px-4 py-4">
            <p className="text-xs font-semibold text-[#9A9491] uppercase tracking-wide mb-3">
              {scannedItems.length} item{scannedItems.length !== 1 ? 's' : ''} scanned
            </p>

            <div className="flex flex-col gap-2 mb-4">
              {scannedItems.map(item => (
                <div key={item.id} className="bg-white rounded-2xl border border-[#F0EDE8] p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl shrink-0">{item.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#2C2A26] truncate">{item.name}</p>
                      <p className="text-xs text-[#9A9491] capitalize">
                        {item.category} · {item.location}
                        {item.isExisting && <span className="text-[#6B9E78] ml-1">· already tracked</span>}
                      </p>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="w-6 h-6 rounded-full bg-[#F2EDE6] flex items-center
                                 justify-center text-[#9A9491] text-xs shrink-0"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    {/* Qty */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => adjustQty(item.id, -1)}
                        className="w-7 h-7 rounded-full bg-[#F2EDE6] text-[#2C2A26] font-bold
                                   flex items-center justify-center text-sm"
                      >
                        −
                      </button>
                      <span className="text-sm font-bold text-[#2C2A26] w-6 text-center">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => adjustQty(item.id, 1)}
                        className="w-7 h-7 rounded-full bg-[#F2EDE6] text-[#2C2A26] font-bold
                                   flex items-center justify-center text-sm"
                      >
                        +
                      </button>
                    </div>

                    {/* Expiry */}
                    <div className="text-right">
                      {item.expiryDate ? (
                        <p className="text-xs font-medium text-[#6B9E78]">
                          Exp: {item.expiryDate}
                        </p>
                      ) : (
                        <p className="text-xs text-[#B0A99F]">No expiry</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  processingRef.current = false
                  setMode('barcode')
                  setStatus('Point camera at a barcode')
                  startBarcodeScanner()
                }}
                className="flex-1 py-3 rounded-xl border border-[#E8E2DC] text-sm
                           font-medium text-[#7A7468]"
              >
                + Scan more
              </button>
              <button
                onClick={handleSaveAll}
                disabled={savingAll || scannedItems.length === 0}
                className="flex-1 py-3 rounded-xl text-white text-sm font-semibold
                           disabled:opacity-50"
                style={{ background: '#6B9E78' }}
              >
                {savingAll ? 'Saving…' : `Add ${scannedItems.length} to pantry`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
